/*
 *  Copyright (c) 2003-2020 GameDuell GmbH, All Rights Reserved
 *  This document is strictly confidential and sole property of GameDuell GmbH, Berlin, Germany
 */
import { createApp, h, reactive } from 'vue'
import 'whatwg-fetch'
import 'url-search-params-polyfill'
import AppHeader from '@/apps/AppHeader.vue'
import AppFooter from '@/apps/AppFooter.vue'
import i18n from '@gd/i18n'
import Toast from '@gd/toast'
import GD from '@gd/classic-gd'
import { Storage as _Storage, GD as _GD, STORE_NAME, STORE } from '@gd/storage'
import { NAV_TYPE, fetchNavigation } from '@gd/shop-navigation'
import { Notifications } from '@gd/notifications'
import mapTabName from './lib/mapTabName'
import GdModal from '@gd/modal'
import { loadTComponents } from '@gd/t-components'
import { shopTracking } from './lib/customTracking'
import events from '@gd/event-bus'
// load styles and let webpack organize the CSS/less imports
import './styles/index.less'
import '@gd/notifications/dist/notifications.css'
import '@gd/common-styles/dist/icf-fonts.less'
import '@gd/toast/dist/toast.css'
import '@gd/accountbox/dist/accountbox.css'
import '@gd/t-components/dist/t-components.css'
import '@gd/modal/dist/modal.css'

const storage: _GD = new _Storage().read(STORE_NAME.GD)
// setup dynamically web context path for production - otherwise the shop-express will be not loaded from stage
if (process.env.NODE_ENV === 'production') {
  __webpack_public_path__ = `${storage.webCtxPath}/classic-web/`
}

// initialize the GD context and merge with existing
;(window as any).GD = Object.assign((window as any).GD || {}, new GD())
// TODO "payment-navigation backward compatibility" : creates the "GD.shop.data"-object so it don't
// failed with the old payment-navigation module
const shared = reactive({
  bearerToken: '',
  showExpress: false,
})

;(window as any).GD.shop.data = shared

const naviTop = document.getElementById('navigation_top')
if (naviTop) {
  /**
   * initialize basic template modules:
   *
   * * accountbox
   * * notifications modules ( awards, newsletter, low money )
   * * ...
   */
  const appHeader = createApp(
    {
      created() {
        // "payment-navigation backward compatibility"
        this.$watch(
          () => shared.showExpress,
          (value) => {
            if (value) {
              events.$emit('deposit')
              ;(window as any).GD.shop.data.showExpress = false
            }
          }
        )

        // register $toast on global
        ;(window as any).GD.$toast = this.$toast

        const storageGD: _GD = new _Storage().read(STORE_NAME.GD)
        if (storageGD.decisions.indexOf('fe-notifications-off') === -1) {
          // NEW: initial ask for notifications.
          // Toast.modals like: awards, newsletter, lowMoney, userBirthday, unlockAvatar, addressUpdate,...
          // needs to be executed after all recources are loaded otherwise some events are registering too late (module loader avatarUnlock)
          // ( seems to be only a problem for redeemAvatar - because it's skipping the rest call fire the event-bus imediately)
          const notifications = new Notifications({
            toast: this.$toast,
            callback: (config) =>
              new Promise((res, rej) => {
                // LOPS-6861 disable tracking
                res(false)
              }),
          })
          window.addEventListener(
            'DOMContentLoaded',
            function domeContentLoaded() {
              window.removeEventListener('DOMContentLoaded', domeContentLoaded)
              notifications.init()
            },
            false
          )
        }

        events.$on('notifications', (data) => {
          /* eslint no-console: "warn" */
          console.warn(
            'notifications:',
            data.map((d: any) => d.type),
            data
          )
        })
        events.$on('update-balance', () => {
          // comment out - otherwise it will be triggered twice
          // appHeader.$emit('update-balance')

          // ToDo: grab full accountbox data, push into global storage and tell interesting modules via event bus
          // for now: just grab the balance and push it as new event, for JSF-signals code
          // ToDo: error handling / 4xx codes etc
          fetch('/gd/rest/me/balance', {
            credentials: 'same-origin',
          })
            .then((res) => res.json())
            .then((data) => {
              events.$emit('balance-updated', data)
            })
        })
        events.$on('deposit', (data?: { trackingId: string; enterShop?: boolean }) => {
          const storage = new _Storage()
          const userId: string = (storage.read(STORE_NAME.USER) as STORE.User).id || ''
          const storageGD = storage.read(STORE_NAME.GD)
          if (data && data.enterShop) {
            shopTracking(userId, (data && data.trackingId) || '', 'SHOP-WEB').finally(() => {
              // go to shop
              ;(window as any).location.href = `${storageGD.webCtxPath}/shop-web/index.html`
            })
            return
          }
          fetchNavigation()
            .then((res: { navType: NAV_TYPE; location?: string }) => {
              if (res.navType === NAV_TYPE.SHOP_EXPRESS) {
                events.$emit('shop-express')
                shopTracking(userId, (data && data.trackingId) || '', 'SHOP-EXPRESS')
              } else {
                shopTracking(userId, (data && data.trackingId) || '', 'SHOP-WEB').finally(() => {
                  // go to shop
                  ;(window as any).location.href = res.location
                  // `${storage.gd.webCtxPath}/shop-web/index.html`
                })
              }
            })
            .catch((err: any) => {
              if (err.location) {
                ;(window as any).location.href = err.location
              } else {
                // reload the page - in most of the cases the user is not logged in
                // TODO add more specific error handling
                ;(window as any).location.reload()
              }
            })
        })
        // for other classic code which using the base.signals:
        document.addEventListener('DOMContentLoaded', function waitForBaseSignals() {
          document.removeEventListener('DOMContentLoaded', waitForBaseSignals)
          if ((window as any).base) {
            const signals: any = (window as any).base.signals
            if (signals && signals.accountBalanceUpdated) {
              signals.accountBalanceUpdated.addListener(() => {
                events.$emit('update-balance')
              })
            }
          }
        })
      },
      render: () => h(AppHeader),
      // the scope will be extended later on to the complete navigation header - for the first it's limitted for
      // createing the accountbox
    },
    {
      theme: naviTop.dataset.theme || new _Storage().read(STORE_NAME.GD).theme,
      selectedTab: mapTabName(naviTop.dataset.tab),
      headerBlank: naviTop.dataset.headerBlank === 'true',
      blackRibbon: naviTop.dataset.blackRibbon === 'blackRibbon',
    }
  )
    // initial toast - use #navigation_top element as pin reference (it exists in mostly all classic
    // pages including lobby-web and tournaments-web) the toast will be placed 20px right side
    // of #navigation_top (if there is enough space)
    .use(Toast, { pin: { node: '#navigation_top', offset: 20 } })
    .use(i18n)
  appHeader.component('gd-modal', GdModal)
  loadTComponents(appHeader)
  appHeader.mount('#navigation_top')
  naviTop.removeAttribute('id')
}
/**
 * TODO continue with:
 * update tournaments-web
 * update classic-web
 */
const footer: HTMLElement | null = document.getElementById('footer')
if (footer) {
  const appFooter = createApp(
    {
      render: () => h(AppFooter),
    },
    {
      footerBlank: footer.dataset.footerBlank === 'true',
    }
  ).use(i18n)
  loadTComponents(appFooter)
  appFooter.mount('#footer')
  footer.removeAttribute('id')
}
